import React, {ChangeEvent, KeyboardEvent} from 'react';
import {inject, observer} from 'mobx-react';
import {ArticlesStore} from 'app/store/ArticlesStore';
import SmartFullPageLoader from 'app/components/reusable/smart_loaders/SmartFullPageLoader';
import LetsShowNews from 'app/components/pages/articles/LetsShowNews';
import ArticlesByCategory from 'app/components/pages/articles/ArticlesByCategory';
import ArticlesByLast from 'app/components/pages/articles/ArticlesByLast';
import I18n from 'app/i18n';
import SearchInput from 'app/components/SearchInput';
import ParamsDataInterface from 'app/interfaces/articles/ParamsDataInterface';
import {articleHeading, isArticlePromotion, isArticleNews} from 'app/utils/ArticleHeading';
import {KEY_ENTER} from 'app/utils/Consts';
import EmptyResults from 'app/components/articles/EmptyResults';

interface Props {
  articlesStore?: ArticlesStore
  type: string
}

@inject('articlesStore')
@observer
export default class ArticlesNewsIndex extends React.Component<Props> {
    state = {search: ''};

    componentDidMount(): void {
        const {type, articlesStore} = this.props;
        this.setState({search: articlesStore.getDataByType(type).searchValue});
    }

    componentWillUnmount = (): void => {
        if (window.location.pathname.includes('/news/')) {
            return;
        }
        this.setState({search: ''});
        this.clearSearch();
    };

    clearSearch = (): void => this.props.articlesStore.setSearchValue('', this.props.type);

    handleRefreshSearch = (): void => {
        this.setState({search: ''});
        this.clearSearch();
        this.loadData();
    };

    searchHandler = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === KEY_ENTER) {
            this.setState({search: event.currentTarget.value});
            this.loadData(null, event.currentTarget.value);
        }
    };

    searchValueHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        this.props.articlesStore.setSearchValue(event.currentTarget.value, this.props.type);
    };

    loadData = (params?: ParamsDataInterface, searchValue = ''): void => {
        this.props.articlesStore.loadData(params, searchValue, this.props.type);
    };

    render(): React.ReactNode {
        const {search} = this.state;
        const {type, articlesStore} = this.props;
        const {loading, searchValue} = articlesStore.getDataByType(type);

        const {esportsData, sportData, casinoData, showEgbData, siteNewsData, lastData} = articlesStore;

        if (loading) {
            return <SmartFullPageLoader />;
        }

        const length = esportsData.data.length + sportData.data.length + casinoData.data.length +
            showEgbData.data.length + siteNewsData.data.length + lastData.data.length;

        return (
            <>
                <div className="content-header">
                    <h1 className="title-h1" key="title">{articleHeading(type)}</h1>
                    {(isArticlePromotion(type) || isArticleNews(type)) && <SearchInput
                        handleKeyPress={this.searchHandler}
                        handleChange={this.searchValueHandler}
                        value={searchValue}
                        onClick={this.handleRefreshSearch}/>}
                </div>
                { search === '' &&
                      <>
                          <LetsShowNews/>
                          <ArticlesByLast articles={lastData.data} type={type} />
                      </>
                }
                { search !== '' && length === 0 && <EmptyResults/> }
                <ArticlesByCategory
                    articles={esportsData.data}
                    type={type}
                    category={I18n.t('menu_links.bets')}
                    slug="bets"
                    spriteIcon="svg-tap_bar_play"
                    containerForMainPage
                />
                <ArticlesByCategory
                    articles={sportData.data}
                    type={type}
                    category={I18n.t('menu_links.sport')}
                    slug="sport"
                    spriteIcon="svg-tap_bar_play_sport"
                    containerForMainPage
                />
                <ArticlesByCategory
                    articles={casinoData.data}
                    type={type}
                    category={I18n.t('menu_links.casino')}
                    slug="casino"
                    spriteIcon="svg-promo-casino"
                    containerForMainPage
                />
                <ArticlesByCategory
                    articles={showEgbData.data}
                    type={type}
                    category={I18n.t('show_egb')}
                    slug="show-egb"
                    spriteIcon="svg-tap_bar_egb"
                    containerForMainPage
                />
                <ArticlesByCategory
                    articles={siteNewsData.data}
                    type={type}
                    category={I18n.t('site_news')}
                    slug="site-news"
                    spriteIcon="svg-tap_bar_news"
                    containerForMainPage
                />
            </>
        );
    }
}
